import Card from '../../../common/card';
import OnboardingDialog from '../../../common/onboarding-dialog';
import { ReactComponent as UserIcon } from '../../../../assets/icons/user-circle.svg';
import { ReactComponent as InfoIcon } from '../../../../assets/icons/info-unfilled.svg';
import { Box, Button } from '@mui/material';
import Input from '../../../common/input';
import l from '../../../../lang';
import Select from '../../../common/select';
import SplitPaymentTerms from '../split-terms';
import InputCheckbox from '../../../common/input-checkbox';
import { useHistory } from 'react-router-dom';
import { Routes } from '../../../../config/routes';
import { useSplitPayments } from '../../../../store/split-payments';
import { Formik } from 'formik';
import { useEffect, useRef } from 'react';
import MessageError from '../../../common/message-error';
import { SplitCategory, SplitUserType } from '../../../../store/split-payments/interfaces';

interface CollaborationInformationPayload {
  name: string;
  email: string;
  category: SplitCategory;
  commission: string;
  terms: boolean;
}

const CollaborationInformation = () => {
  const history = useHistory();
  const errorElementRef = useRef<HTMLDivElement | null>(null);
  const { validateMerchantCollaborator, collaboratorEmailValidated, errorInfo, error, userType } = useSplitPayments();

  useEffect(() => {
    if (collaboratorEmailValidated) {
      history.push(Routes.SPLIT_PAYMENTS_STEP_RESUME);
    }
  }, [collaboratorEmailValidated]);

  useEffect(() => {
    if (error && errorElementRef.current) {
      errorElementRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [error]);

  const initialValues = {
    name: '',
    email: '',
    category: SplitCategory.PARTNER,
    commission: '',
    terms: false,
  } as CollaborationInformationPayload;

  const validate = (values: CollaborationInformationPayload) => {
    const errors: Record<string, string> = {};

    if (!values.name) {
      errors.name = l('split_payments.creation.required.name');
    }

    if (!values.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
      errors.email = l('split_payments.creation.valid.email');
    }

    if (!values.category) {
      errors.category = l('split_payments.creation.required.category');
    }
    const commission = Number(values.commission);
    if (!values.commission || isNaN(commission) || commission < 0.01 || commission > 100) {
      errors.commission = l('split_payments.creation.valid.commission');
    }
    if (!values.terms) {
      errors.terms = l('split_payments.creation.accepted.terms');
    }

    return errors;
  };

  const handleNext = (values: CollaborationInformationPayload) => {
    validateMerchantCollaborator({
      name: values.name,
      email: values.email,
      collaborationCategory: values.category,
      collaboratorCommission: Number(values.commission),
      code: '',
    });
  };

  return (
    <OnboardingDialog
      dividers={false}
      unpaddingContent
      unpaddingFooter
      fullScreenMobile
      type="md"
      bgColor="#F9FAFB"
      title={`${l('split_payments.title')} - ${l('split_payments.creation.invite')}`}
      body={
        <>
          <div style={{ marginTop: '6em', marginLeft: '1em', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)' }}>
            <div style={{ gridColumn: '2 / 3' }}>
              <Card>
                {error && (
                  <Box ref={errorElementRef} sx={{ marginBottom: '20px' }}>
                    <MessageError errorInfo={errorInfo} />
                  </Box>
                )}
                <Formik initialValues={initialValues} validate={validate} onSubmit={values => handleNext(values)}>
                  {({ handleSubmit, handleBlur, handleChange, errors, setFieldValue, values }) => {
                    return (
                      <form onSubmit={handleSubmit}>
                        <div className="transaction_detail__title_section">
                          <UserIcon className="transaction_detail__title_section__icon" />
                          {l('split_payments.creation.menu.collaboration_info')}
                        </div>
                        <Input
                          type="text"
                          name="name"
                          label={l('split_payments.creation.name')}
                          onFocus={() => {}}
                          value={values.name}
                          error={errors.name}
                          onBlur={handleBlur}
                          onChange={handleChange}
                        />
                        <Input
                          type="text"
                          name="email"
                          label={
                            userType === SplitUserType.COLLABORATOR
                              ? l('split_payments.creation.collaboratorEmail')
                              : l('split_payments.creation.sellerEmail')
                          }
                          message={l('split_payments.creation.email.desc')}
                          onFocus={() => {}}
                          value={values.email}
                          error={errors.email}
                          onBlur={handleBlur}
                          onChange={handleChange}
                        />
                        <Select
                          name="category"
                          options={{
                            [SplitCategory.PARTNER]: l('split_payments.creation.category.partner'),
                            [SplitCategory.AGENCY]: l('split_payments.creation.category.agency'),
                            [SplitCategory.INDEPENDENT_DEVELOPER]: l(
                              'split_payments.creation.category.independent_developer'
                            ),
                            [SplitCategory.MARKETPLACE]: l('split_payments.creation.category.marketplace'),
                          }}
                          value={values.category}
                          error={errors.category}
                          onBlur={handleBlur}
                          onChange={handleChange}
                        />
                        <Input
                          type="number"
                          name="commission"
                          label={l('split_payments.creation.commission')}
                          placeholder={l('split_payments.creation.commission.placeholder')}
                          onFocus={() => {}}
                          value={values.commission}
                          error={errors.commission}
                          onBlur={handleBlur}
                          onChange={handleChange}
                        />
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginTop: '8px',
                            color: '#637381',
                            fontSize: '14px',
                          }}
                        >
                          <InfoIcon width={14} height={14} />
                          <span>{l('split_payments.creation.commission.tooltip')}</span>
                        </div>
                        <div style={{ marginTop: '20px' }} className="transaction_detail__title_section">
                          <UserIcon className="transaction_detail__title_section__icon" />
                          {l('split_payments.creation.collab_terms_conditions')}
                        </div>
                        <SplitPaymentTerms
                          title={l('split_payments.creation.terms_conditions')}
                          htmlContent={l('split_payments.terms_and_conditions.body')}
                          maxHeight={400}
                        ></SplitPaymentTerms>
                        <InputCheckbox
                          name="terms"
                          label={l('split_payments.creation.read_terms')}
                          value={values.terms}
                          error={errors.terms}
                          onBlur={handleBlur}
                          onChange={handleChange}
                        />

                        <div className="split-payments__user-type__buttons">
                          <Button
                            variant="contained"
                            size="large"
                            sx={{ backgroundColor: '#E8E8E8', color: 'black', ':hover': { backgroundColor: '#D0D0D0' } }}
                            onClick={() => history.goBack()}
                          >
                            {l('common.cancel')}
                          </Button>
                          <Button variant="contained" size="large" type="submit">
                            {l('common.next')}
                          </Button>
                        </div>
                      </form>
                    );
                  }}
                </Formik>
              </Card>
            </div>
          </div>
        </>
      }
      menuItems={[
        { title: l('split_payments.creation.menu.introduction'), completed: true, selected: false },
        {
          title: l('split_payments.creation.user_type'),
          subItems: [
            {
              title: l('split_payments.creation.menu.seller_collab'),
              completed: true,
              selected: false,
            },
          ],
        },
        {
          title: l('split_payments.creation.menu.collaboration_info'),
          subItems: [
            {
              title: l('split_payments.creation.menu.collaboration_info'),
              completed: false,
              selected: true,
            },
          ],
        },
        {
          title: l('split_payments.creation.menu.terms_and_conditions'),
          subItems: [
            {
              title: l('split_payments.creation.menu.terms_and_conditions_approve'),
              completed: false,
              selected: true,
            },
            {
              title: l('split_payments.creation.menu.agreement'),
              completed: false,
              selected: false,
            },
          ],
        },
      ]}
    />
  );
};

export default CollaborationInformation;
